import { UseMutationOptions, useMutation } from '@tanstack/react-query'

import { peachApi } from 'core/api'
import { reportOperationError } from 'core/helpers/error'

import type { OperationData } from '../makeMutationHook'
import {
  committedMembersListCsv,
  draftPopulationMembersListCsv,
  getDisasterImpactedAreasCsv,
  supercaseMembersTemplateCsv,
} from '../operations'

type Options<Resp, Vars> = Vars & {
  options?: UseMutationOptions<Resp>
}

const makeDownloadHook =
  <Resp, Key, Vars extends { [index: string]: unknown; query?: { [index: string]: unknown } | void }>({
    summary,
    method,
    path,
  }: OperationData<Resp, Key, Vars>) =>
  ({ options, ...args }: Options<Resp, Vars>) =>
    useMutation<Resp>({
      mutationFn: () =>
        peachApi.download({
          query: (args as any).query,
          method,
          url: path.replace(/\{(\w+)\}/g, (_, p1) => {
            const variable = (args as { [key: string]: unknown })[p1]

            if (!variable) {
              throw new Error(`Path variable "${p1}" is not defined.`)
            }

            if (
              typeof variable === 'string' ||
              typeof variable === 'number' ||
              typeof variable === 'boolean' ||
              typeof variable === 'bigint'
            ) {
              return String(variable)
            }

            throw new Error(`Path variable "${p1}" must be a primitive value.`)
          }),
        }),
      ...options,
      onError: (e) => {
        reportOperationError(summary, e)
        console.error(e)
      },
    })

export const useDownloadCommittedMembersList = makeDownloadHook(committedMembersListCsv)

export const useDownloadDraftMembersList = makeDownloadHook(draftPopulationMembersListCsv)

export const useDownloadImpactedAreas = makeDownloadHook(getDisasterImpactedAreasCsv)

export const useDownloadMembersTemplate = makeDownloadHook(supercaseMembersTemplateCsv)
